import React, { useState } from "react";
import { TextField, Autocomplete, Chip, Box, Popover, Button, Typography, Divider, Grid, Tooltip, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from '@mui/icons-material/Clear';
import { connect } from "react-redux";
import { addSearchFilter, removeSearchFilter, clearSearchFilters, setDateRange, setCorrectionType } from "../../../store/job/jobSlice";
import FilterListIcon from '@mui/icons-material/FilterList';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";

const correctionOptions = [
  "Product / Imprint Color",
  "Item# Adjustment",
  "Grammar / Spelling / Font / Size adjustments",
  "New Logo",
  "Stitch count adjustment",
  "Multi Changes / Other",
  "Other",
  "Correction",
  "Revision",
];

const Search = ({
  search_columns = {},
  searchFilters,
  addSearchFilter,
  removeSearchFilter,
  clearSearchFilters,
  onSearchSubmit,
  fromDate,
  toDate,
  setDateRange,
  setCorrectionType,
  correctionType = []
}) => {
  const [anchorElCorrection, setAnchorElCorrection] = useState(null);
  const openCorrection = Boolean(anchorElCorrection);

  const [localFromDate, setLocalFromDate] = useState(fromDate);
  const [localToDate, setLocalToDate] = useState(toDate);
  const [localCorrectionType, setLocalCorrectionType] = useState(correctionType);

  // Sync local state when popover opens
  React.useEffect(() => {
    if (openCorrection) {
      setLocalFromDate(fromDate);
      setLocalToDate(toDate);
      setLocalCorrectionType(correctionType);
    }
  }, [openCorrection, fromDate, toDate, correctionType]);
  // Transform search_columns into options
  // Handles both array (['col1', 'col2']) and object ({ col1: 'Label1' }) formats
  const columnOptions = React.useMemo(() => {
    const options = [];

    if (Array.isArray(search_columns)) {
      search_columns.forEach(col => {
        if (typeof col === 'string') {
          options.push({
            key: col,
            label: col.replace(/_/g, ' ')
          });
        } else {
          options.push({
            key: col.key,
            label: col.label || col.key
          });
        }
      });
    } else {
      Object.entries(search_columns).forEach(([key, label]) => {
        options.push({ key, label });
      });
    }

    // Filter out duplicates (if any)
    const seen = new Set();
    return options.filter(opt => {
      if (seen.has(opt.key)) return false;
      seen.add(opt.key);
      return true;
    });
  }, [search_columns]);

  const [inputValue, setInputValue] = React.useState("");

  const handleAddFilter = (column, value) => {
    if (!value?.trim()) return;

    const filter = {
      value: value.trim(),
      key: column?.key || null, // Use null for global/keyless search
      label: column?.label || "Search",
    };

    addSearchFilter(filter);
    setInputValue("");
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleApplyFilters = () => {
    setDateRange({ fromDate: localFromDate, toDate: localToDate });
    setCorrectionType(localCorrectionType);
    setAnchorElCorrection(null);
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleClearModalFilters = () => {
    setLocalFromDate(null);
    setLocalToDate(null);
    setLocalCorrectionType([]);
  };

  const handleClearAllFilters = () => {
    clearSearchFilters();
    setDateRange({ fromDate: null, toDate: null });
    setCorrectionType([]);
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleDelete = (filter) => {
    removeSearchFilter(filter);
  };

  const filterColumnOptions = (options) => options;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Autocomplete
          multiple
          freeSolo
          options={columnOptions}
          filterOptions={filterColumnOptions}
          value={searchFilters}
          inputValue={inputValue}
          openOnFocus={false} // Prevent dropdown from opening automatically on focus (e.g. when popover closes)
          forcePopupIcon={false} // Hide the down arrow icon
          fullWidth
          onInputChange={(e, newInput) => {
            setInputValue(newInput);
          }}
          onChange={(e, newValue, reason, details) => {
            if (reason === "selectOption" && details?.option) {
              handleAddFilter(details.option, inputValue);
              setInputValue("");
            } else if (reason === "removeOption") {
            } else if (reason === "clear") {
              clearSearchFilters();
              setInputValue("");
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && inputValue.trim()) {
              e.preventDefault();
              handleAddFilter(null, inputValue);
              setInputValue("");
            }
          }}
          getOptionLabel={(option) => option.label || ""}
          renderTags={(value, getTagProps) =>
            value.map((filter, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${filter.key}-${filter.value}`}
                label={
                  filter.key && filter.label
                    ? `${filter.label}: ${filter.value}`
                    : filter.value
                }
                onDelete={() => handleDelete(filter)}
              />
            ))
          }
          renderInput={(params) => (
            <Box sx={{ position: 'relative', width: '100%' }}>
              <TextField
                {...params}
                placeholder="Search..."
                variant="standard"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: null, // We are placing the icon manually
                  sx: {
                    fontSize: '0.95rem',
                    color: '#666',
                    paddingLeft: '10px',
                    // Ensure chips don't overlap with the search icon
                    paddingRight: '70px',
                    '&::placeholder': {
                      color: '#999',
                      opacity: 1
                    }
                  }
                }}
                sx={{
                  '& .MuiInput-root': {
                    borderBottom: '2px solid #b6b4b4ff',
                    paddingBottom: '8px',
                    borderRadius: '5px',
                    '&:hover': {
                      borderBottom: '2px solid #ccc',
                    },
                    '&.Mui-focused': {
                      borderBottom: '2px solid black',
                    }
                  }
                }}
              />
              <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                <SearchIcon
                  sx={{
                    color: 'black',
                    cursor: 'pointer',
                    fontSize: '28px',
                    zIndex: 1,
                    mr: 1
                  }}
                  onClick={() => {
                    if (inputValue.trim()) {
                      handleAddFilter(null, inputValue);
                      setInputValue("");
                    }
                  }}
                />
                <FilterListIcon
                  sx={{
                    color: (correctionType?.length > 0 || fromDate || toDate) ? '#ff6100' : 'black',
                    cursor: 'pointer',
                    fontSize: '28px',
                    zIndex: 1,
                    mr: 1
                  }}
                  onClick={(e) => setAnchorElCorrection(e.currentTarget)}
                />
                <Tooltip title="Clear All Filters">
                  <ClearIcon
                    sx={{
                      color: 'black',
                      cursor: 'pointer',
                      fontSize: '26px',
                      zIndex: 1
                    }}
                    onClick={handleClearAllFilters}
                  />
                </Tooltip>
              </Box>
            </Box>
          )}
        />
      </Box>
      <Popover
        open={openCorrection}
        anchorEl={anchorElCorrection}
        onClose={() => setAnchorElCorrection(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { borderRadius: '12px', mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
        }}
      >
        <Box sx={{ p: 3, width: 450 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              Filters
            </Typography>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={() => setAnchorElCorrection(null)}
                sx={{ position: 'absolute', right: -8 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#666', mb: 1 }}>
            Revision Type
          </Typography>
          <Autocomplete
            multiple
            options={correctionOptions}
            value={localCorrectionType}
            onChange={(event, newValue) => setLocalCorrectionType(newValue)}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Types..." size="small" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="filled" label={option} size="small" {...getTagProps({ index })} sx={{ backgroundColor: '#ff6100', color: 'white' }} />
              ))
            }
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#666', mb: 1.5 }}>
            Submitted Date
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="From Date"
                  value={localFromDate ? dayjs(localFromDate) : null}
                  onChange={(newValue) => setLocalFromDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="To Date"
                  value={localToDate ? dayjs(localToDate) : null}
                  onChange={(newValue) => setLocalToDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => {
                handleClearModalFilters();
                // If they click clear we also reset the global state for these specific fields on apply or now?
                // The user asked for "option to clear both correction type and date". 
                // I'll make this clear button reset the global state too for immediate feedback.
                setDateRange({ fromDate: null, toDate: null });
                setCorrectionType([]);
                setAnchorElCorrection(null);
                if (onSearchSubmit) onSearchSubmit();
              }}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              Clear All Filters
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#ff6100',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#e55600' }
              }}
            >
              Apply Filter
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box >
  );
};

const mapStateToProps = (state) => ({
  searchFilters: state.job.searchFilters,
  search_columns: state.job.search_columns,
  fromDate: state.job.fromDate,
  toDate: state.job.toDate,
  correctionType: state.job.correctionType,
});

const mapDispatchToProps = {
  addSearchFilter,
  removeSearchFilter,
  clearSearchFilters,
  setDateRange,
  setCorrectionType,
};

export default connect(mapStateToProps, mapDispatchToProps)(Search);